import type { ApprovalDecision, BizyChatAssistantMessage, BizyChatMessage, FeedbackType } from '../types/chat';
import type { BizyAssistantMessageNode } from '../types/nodes';

// Default Bizy "emotion" states. Plain strings so the package stays free of any
// host-specific enum; hosts map these onto their own avatar states.
export const BIZY_EMOTION_DEFAULT = 'curious';
export const BIZY_EMOTION_DISAPPOINTED = 'disappointed';

/**
 * Renderable chat-pane state owned by the connector. The hook keeps the
 * high-frequency streaming accumulators (token-by-seq buffers, document nodes,
 * timeouts) in refs; this reducer is the part that drives re-renders.
 *
 * Model: the reducer owns the **full** message list (optimistic in-flight
 * bubbles live at the tail alongside finalized turns), mirroring the web
 * sidebar's existing behavior.
 */
export interface BizyChatPaneState {
	messages: BizyChatMessage[];
	isLoading: boolean;
	isTimedOut: boolean;
	isOtherTabLoading: boolean;
	bizyEmotion: string;
	activeToolCalls: string[];
}

export const initialBizyChatPaneState: BizyChatPaneState = {
	messages: [],
	isLoading: false,
	isTimedOut: false,
	isOtherTabLoading: false,
	bizyEmotion: BIZY_EMOTION_DEFAULT,
	activeToolCalls: [],
};

export type BizyChatPaneAction =
	/** Replace the whole list (hydration / conversation load). */
	| { type: 'load'; messages: BizyChatMessage[]; bizyEmotion?: string }
	/** Prepend older messages at the head of the list (infinite scroll history load). Does not touch loading/emotion state. */
	| { type: 'prepend'; messages: BizyChatMessage[] }
	/** Full reset (new chat). */
	| { type: 'reset' }
	/** Optimistic send: push the user bubble and enter the loading state. */
	| { type: 'send'; message: BizyChatUserMessageInput }
	/** Streaming markdown text for the open assistant turn (already longest-wins resolved). */
	| { type: 'token'; content: string }
	/** Snapshot of the typed document for the open (un-finalized) assistant turn. */
	| { type: 'assistant-nodes'; nodes: BizyAssistantMessageNode[] }
	/** Snapshot for a `node_appended` — merges onto the last assistant whether open OR just finalized (late cards). */
	| { type: 'merge-nodes'; nodes: BizyAssistantMessageNode[] }
	/** A tool started running. */
	| { type: 'tool-call'; toolName: string }
	/** Finalize the open assistant turn. */
	| { type: 'complete'; content: string; messageId?: string; nodes?: BizyAssistantMessageNode[]; toolsUsed?: string[]; emotion?: string | null }
	/** Conductor finished but a connector tool is still running — strip the half-answer, stay loading. */
	| { type: 'connector-processing' }
	/** A connector tool produced the final (node-less) answer. */
	| { type: 'connector-response'; content: string; messageId?: string }
	/** Stream errored — drop the partial answer, surface retry. */
	| { type: 'error' }
	/** User/!server cancelled — drop the in-flight bubbles. */
	| { type: 'cancelled' }
	/** Conversation handed off to human support — drop the partial answer, stop loading, keep the user message. */
	| { type: 'support-escalated' }
	/** Another tab owns this conversation (HTTP 409) — drop the optimistic user bubble. */
	| { type: 'busy-409' }
	/** Stream timed out — surface retry. */
	| { type: 'timeout' }
	/** Re-send the last message: drop the partial answer, re-enter loading, clear retry/error. */
	| { type: 'retry-start' }
	/** Cross-tab awareness toggle. */
	| { type: 'other-tab-loading'; loading: boolean }
	/** Drop a trailing un-persisted assistant bubble (retry / mount self-heal). */
	| { type: 'drop-open-assistant' }
	/** Record per-message feedback. */
	| { type: 'update-feedback'; messageId: string; feedbackType: FeedbackType }
	/** Record a per-card choice so post-click state survives reloads. */
	| { type: 'update-node-choice'; messageKey: string; cardIndex: number; choice: ApprovalDecision };

export interface BizyChatUserMessageInput {
	key: string;
	content: string;
	clientId?: string;
	timestamp?: Date;
	attachmentPreviewUrl?: string;
}

// The streaming assistant turn is "open" until `complete` stamps it with an id.
function isOpenAssistant(message: BizyChatMessage | undefined): message is BizyChatAssistantMessage {
	return message?.role === 'assistant' && !message.id;
}

function lastMessage(messages: BizyChatMessage[]): BizyChatMessage | undefined {
	return messages[messages.length - 1];
}

// Append/merge onto the open assistant turn, or create one if there isn't an
// open turn at the tail (mirrors the web hook's "create on first token").
function upsertOpenAssistant(
	messages: BizyChatMessage[],
	update: (assistant: BizyChatAssistantMessage) => BizyChatAssistantMessage,
	makeKey: () => string
): BizyChatMessage[] {
	const last = lastMessage(messages);
	if (isOpenAssistant(last)) {
		return [...messages.slice(0, -1), update(last)];
	}
	return [...messages, update({ role: 'assistant', key: makeKey(), content: '', nodes: [] })];
}

// Merge onto the last assistant turn whether it's still open OR already
// finalized (a late `node_appended` from the just-completed turn lands on the
// message it belongs to, not a fresh bubble), or create one if there's none.
function upsertLastAssistant(
	messages: BizyChatMessage[],
	update: (assistant: BizyChatAssistantMessage) => BizyChatAssistantMessage,
	makeKey: () => string
): BizyChatMessage[] {
	const last = lastMessage(messages);
	if (last?.role === 'assistant') {
		return [...messages.slice(0, -1), update(last)];
	}
	return [...messages, update({ role: 'assistant', key: makeKey(), content: '', nodes: [] })];
}

// Drop a trailing un-persisted assistant bubble, if present.
function dropOpenAssistant(messages: BizyChatMessage[]): BizyChatMessage[] {
	return isOpenAssistant(lastMessage(messages)) ? messages.slice(0, -1) : messages;
}

/**
 * Pure reducer for one Bizy chat surface. `makeKey` mints stable per-bubble
 * keys for lazily-created assistant turns; the hook supplies a monotonic
 * generator so the reducer stays deterministic and host-agnostic.
 */
export function bizyChatPaneReducer(state: BizyChatPaneState, action: BizyChatPaneAction, makeKey: () => string): BizyChatPaneState {
	switch (action.type) {
		case 'load':
			return {
				...initialBizyChatPaneState,
				messages: action.messages,
				bizyEmotion: action.bizyEmotion ?? BIZY_EMOTION_DEFAULT,
			};

		case 'prepend':
			// Older messages from infinite-scroll history go to the head. Loading
			// state, emotion, and streaming accumulators are intentionally untouched.
			return { ...state, messages: [...action.messages, ...state.messages] };

		case 'reset':
			return { ...initialBizyChatPaneState };

		case 'send':
			return {
				...state,
				messages: [...state.messages, { role: 'user', ...action.message }],
				isLoading: true,
				isTimedOut: false,
				isOtherTabLoading: false,
				activeToolCalls: [],
			};

		case 'token':
			return {
				...state,
				messages: upsertOpenAssistant(state.messages, (a) => ({ ...a, content: action.content }), makeKey),
			};

		case 'assistant-nodes':
			return {
				...state,
				messages: upsertOpenAssistant(state.messages, (a) => ({ ...a, nodes: action.nodes }), makeKey),
			};

		case 'merge-nodes':
			return {
				...state,
				messages: upsertLastAssistant(state.messages, (a) => ({ ...a, nodes: action.nodes }), makeKey),
			};

		case 'tool-call':
			return state.activeToolCalls.includes(action.toolName) ? state : { ...state, activeToolCalls: [...state.activeToolCalls, action.toolName] };

		case 'complete': {
			const { content, messageId, nodes, toolsUsed, emotion } = action;
			const last = lastMessage(state.messages);
			const apply = (a: BizyChatAssistantMessage): BizyChatAssistantMessage => ({
				...a,
				content,
				id: messageId || undefined,
				nodes: nodes ?? a.nodes,
				toolsUsed: toolsUsed ?? a.toolsUsed,
			});
			// Finalize the last assistant turn whether it's still open OR was just
			// created — a late card from the same turn lands on the right bubble.
			const messages =
				last?.role === 'assistant'
					? [...state.messages.slice(0, -1), apply(last)]
					: [...state.messages, apply({ role: 'assistant', key: makeKey(), content: '', nodes: [] })];
			return {
				...state,
				messages,
				isLoading: false,
				isOtherTabLoading: false,
				isTimedOut: false,
				activeToolCalls: [],
				bizyEmotion: emotion || BIZY_EMOTION_DEFAULT,
			};
		}

		case 'connector-processing':
			// Strip the conductor's half-answer; keep loading so the thinking
			// indicator stays up while the connector tool runs.
			return {
				...state,
				messages: dropOpenAssistant(state.messages),
				activeToolCalls: [],
			};

		case 'connector-response': {
			const { content, messageId } = action;
			const last = lastMessage(state.messages);
			const apply = (a: BizyChatAssistantMessage): BizyChatAssistantMessage => ({ ...a, content, id: messageId || a.id });
			const fresh: BizyChatAssistantMessage = { role: 'assistant', key: makeKey(), content, id: messageId || undefined, nodes: [] };
			const messages =
				last?.role === 'assistant' && last.id === (messageId || undefined) ? [...state.messages.slice(0, -1), apply(last)] : [...state.messages, fresh];
			return { ...state, messages, isLoading: false, isOtherTabLoading: false, isTimedOut: false, activeToolCalls: [] };
		}

		case 'error':
			// Drop the partial assistant bubble (it was never persisted); keep the
			// user message visible for retry.
			return {
				...state,
				messages: dropOpenAssistant(state.messages),
				isLoading: false,
				isTimedOut: true,
				isOtherTabLoading: false,
				activeToolCalls: [],
				bizyEmotion: BIZY_EMOTION_DISAPPOINTED,
			};

		case 'cancelled': {
			// Drop the in-flight pair: the open assistant and its user bubble.
			let messages = state.messages;
			const last = lastMessage(messages);
			if (isOpenAssistant(last)) {
				messages = messages.slice(0, -1);
				if (lastMessage(messages)?.role === 'user') messages = messages.slice(0, -1);
			} else if (last?.role === 'user') {
				messages = messages.slice(0, -1);
			}
			return { ...state, messages, isLoading: false, isOtherTabLoading: false, activeToolCalls: [] };
		}

		case 'support-escalated':
			// Graceful terminal: drop the unpersisted partial answer and stop
			// loading, but keep the user message and don't surface retry/error.
			return {
				...state,
				messages: dropOpenAssistant(state.messages),
				isLoading: false,
				isOtherTabLoading: false,
				activeToolCalls: [],
			};

		case 'busy-409': {
			// Another tab owns the conversation — remove the optimistic user bubble
			// and reflect the other tab working instead of an error.
			const messages = lastMessage(state.messages)?.role === 'user' ? state.messages.slice(0, -1) : state.messages;
			return { ...state, messages, isLoading: false, isOtherTabLoading: true, activeToolCalls: [] };
		}

		case 'timeout':
			return { ...state, isLoading: false, isTimedOut: true, activeToolCalls: [] };

		case 'retry-start':
			return {
				...state,
				messages: dropOpenAssistant(state.messages),
				isLoading: true,
				isTimedOut: false,
				isOtherTabLoading: false,
				activeToolCalls: [],
			};

		case 'other-tab-loading':
			return { ...state, isOtherTabLoading: action.loading };

		case 'drop-open-assistant':
			return { ...state, messages: dropOpenAssistant(state.messages) };

		case 'update-feedback':
			return {
				...state,
				messages: state.messages.map((m) => (m.role === 'assistant' && m.id === action.messageId ? { ...m, feedbackType: action.feedbackType } : m)),
			};

		case 'update-node-choice':
			return {
				...state,
				messages: state.messages.map((m) =>
					m.key === action.messageKey && m.role === 'assistant' ? { ...m, nodeChoices: { ...(m.nodeChoices ?? {}), [action.cardIndex]: action.choice } } : m
				),
			};
	}
}
